import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MailCheck, Loader2, RefreshCw, Check } from 'lucide-react';
import api from '../../services/api';

// ---------------------------------------------------------------------------
// "Enter the six digits we just emailed you."
//
// SIX BOXES, NOT ONE FIELD. A code is copied a digit at a time from a
// notification, and a single input makes somebody check their own typing.
// Boxes make a wrong digit obvious without reading anything back.
//
// Paste works on the whole code, because most people copy all six at once from
// the notification and pasting into box one should not put "074218" in it.
//
// The button is not the only way to submit: filling the last box submits, so
// the common path never needs a press.
// ---------------------------------------------------------------------------

const LENGTH = 6;

// TWO CALLERS, TWO ENDPOINTS.
//
// During signup there is no account yet and therefore no token: the sealed
// ticket the caller is holding plus the code IS the credential, and
// /auth/signup/verify is what creates the account. Changing the address later
// happens inside a session, where the token identifies who is asking.
//
// `signup` chooses between them, and the caller owns the ticket because a
// resend replaces it. The boxes, the paste handling and the cooldown are the
// same either way.
export default function VerifyEmail({
  email, purpose = 'signup', onVerified, onCancel, signup = false, onVerify, onResend
}) {
  const [digits, setDigits] = useState(Array(LENGTH).fill(''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const boxes = useRef([]);
  const submitted = useRef(false);

  useEffect(() => { boxes.current[0]?.focus(); }, []);

  // A visible countdown, so "resend" is never a button that silently refuses.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = useCallback(async (code) => {
    if (busy || code.length !== LENGTH) return;
    setBusy(true);
    setError('');
    try {
      const { data } = signup
        ? await onVerify({ code })
        : await api.post('/auth/email/verify', { code });
      onVerified?.(data);
    } catch (err) {
      setError(err?.error || 'That could not be checked. Try again.');
      setDigits(Array(LENGTH).fill(''));
      boxes.current[0]?.focus();
      submitted.current = false;
    } finally {
      setBusy(false);
    }
  }, [busy, onVerified, signup, onVerify]);

  const put = (index, value) => {
    const clean = value.replace(/\D/g, '');
    if (!clean) {
      setDigits((d) => { const n = [...d]; n[index] = ''; return n; });
      return;
    }

    setDigits((d) => {
      const next = [...d];
      // A paste of the whole code lands in whichever box was focused; spread
      // it across the rest rather than cramming it into one.
      clean.split('').forEach((ch, i) => {
        if (index + i < LENGTH) next[index + i] = ch;
      });
      const filled = Math.min(index + clean.length, LENGTH - 1);
      boxes.current[filled]?.focus();

      const joined = next.join('');
      if (joined.length === LENGTH && !next.includes('') && !submitted.current) {
        submitted.current = true;
        setTimeout(() => submit(joined), 0);
      }
      return next;
    });
  };

  const onKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      boxes.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) boxes.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < LENGTH - 1) boxes.current[index + 1]?.focus();
  };

  const resend = async () => {
    setError('');
    setSent('');
    try {
      const { data } = signup
        ? await onResend()
        : await api.post('/auth/email/send-code', {});
      setSent(`Sent to ${data?.to || email}`);
      setCooldown(60);
    } catch (err) {
      const seconds = Number((/(\d+) seconds/.exec(err?.error || '') || [])[1]);
      if (seconds) setCooldown(seconds);
      setError(err?.error || 'The code could not be sent.');
    }
  };

  return (
    <div className="text-center">
      <span className="inline-flex w-14 h-14 rounded-2xl bg-brand-500/10 text-brand-500
                       items-center justify-center mb-4">
        <MailCheck size={26} />
      </span>

      <h2 className="text-xl font-bold text-ink">
        {purpose === 'change' ? 'Confirm your new email' : 'Confirm your email'}
      </h2>
      <p className="text-sm text-ink-2 mt-1.5">
        We sent a six-digit code to{' '}
        <strong className="text-ink break-all">{email}</strong>.
      </p>

      <div className="flex justify-center gap-2 mt-6" dir="ltr">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { boxes.current[i] = el; }}
            value={d}
            onChange={(e) => put(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onFocus={(e) => e.target.select()}
            // `inputMode` is what raises the digit keypad on a phone; `type`
            // stays text so the field never shows spinner arrows.
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={LENGTH}
            aria-label={`Digit ${i + 1}`}
            disabled={busy}
            className="w-11 h-14 text-center text-xl font-bold rounded-xl border-[1px] border-line
                       bg-surface text-ink focus:outline-none focus:border-brand-500
                       focus-visible:ring-2 focus-visible:ring-brand-500/40 transition
                       disabled:opacity-60"
          />
        ))}
      </div>

      {error && <p className="text-sm text-rose-600 mt-4">{error}</p>}
      {sent && !error && (
        <p className="text-sm text-emerald-600 mt-4 inline-flex items-center gap-1.5">
          <Check size={14} /> {sent}
        </p>
      )}

      <button
        type="button"
        onClick={() => submit(digits.join(''))}
        disabled={busy || digits.includes('')}
        className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-lg
                   bg-brand-gradient text-white font-semibold px-4 py-2.5 text-sm
                   hover:shadow-glow transition disabled:opacity-50"
      >
        {busy ? <><Loader2 size={15} className="animate-spin" /> Checking…</> : 'Confirm'}
      </button>

      <div className="flex items-center justify-center gap-4 mt-4 text-xs">
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0}
          className="inline-flex items-center gap-1.5 text-ink-2 hover:text-ink py-2
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw size={13} />
          {cooldown > 0 ? `Send again in ${cooldown}s` : 'Send another code'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-ink-3 hover:text-ink py-2">
            Cancel
          </button>
        )}
      </div>

      <p className="text-[11px] text-ink-3 mt-4">
        Check spam if it has not arrived. The code expires in 10 minutes.
      </p>
    </div>
  );
}
